import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import { 
  ArrowBack, 
  AddCircleOutline, 
  PersonSearch as PersonSearchIcon,
  MonitorHeart, 
  Healing, 
  SettingsInputComponent,
  Pets as PetsIcon 
} from '@mui/icons-material';
import api from '../api/api';
import dayjs from 'dayjs';

// Componentes hijos
import RegistroModal from '../components/hospitalizacion/RegistroModal';
import EvolucionCard from '../components/hospitalizacion/EvolucionCard';
import BalanceHidricoCard from '../components/hospitalizacion/BalanceHidricoCard';
import MedicacionCard from '../components/hospitalizacion/MedicacionCard';
import NutricionCard from '../components/hospitalizacion/NutricionCard';
import AccesosInvasivosCard from '../components/hospitalizacion/AccesosInvasivosCard';

const HospitalizacionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const [hospitalizacion, setHospitalizacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alergias, setAlergias] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const resHosp = await api.get(`/hospitalizaciones/${id}`);
      setHospitalizacion(resHosp.data);
      if (resHosp.data?.id_paciente) {
        try {
          const resAlergias = await api.get(`/alergias-pacientes/paciente/${resHosp.data.id_paciente}`);
          setAlergias(resAlergias.data || []);
        } catch (e) { console.warn(e); }
      }
    } catch (err) {
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f1f5f9', minHeight: '100vh' }}>
      
      {/* HEADER COMPACTO */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hospitalizados-lista')} variant="text">
          Volver
        </Button>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={() => setOpenModal(true)}
          sx={{ borderRadius: 2 }}
        >
          Registrar Actividad Clínica
        </Button>
      </Box>

      {/* FICHA TÉCNICA DEL PACIENTE */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}><PetsIcon /></Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" fontWeight="bold">{hospitalizacion?.nombre_paciente}</Typography>
                <Tooltip title="Ver Propietario">
                  <IconButton 
                    size="small" 
                    onClick={() => window.open(`/clientes/${hospitalizacion?.id_cliente}`, '_blank')}
                    sx={{ bgcolor: 'action.hover' }}
                  >
                    <PersonSearchIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {hospitalizacion?.especie} • {hospitalizacion?.raza} • <strong>Ingreso:</strong> {dayjs(hospitalizacion?.fecha_ingreso).format('hh:mm A')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { md: 'flex-end' } }}>
              {alergias.length > 0 ? alergias.map(al => (
                <Chip key={al.id_alergia_paciente} label={al.descripcion_alergia} color="error" size="small" />
              )) : <Chip label="Sin alergias registradas" size="small" variant="outlined" disabled />}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* SISTEMA DE 3 PESTAÑAS */}
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}
        >
          <Tab icon={<MonitorHeart />} iconPosition="start" label="Evolución y Balance" sx={{ py: 2 }} />
          <Tab icon={<Healing />} iconPosition="start" label="Medicación y Nutrición" sx={{ py: 2 }} />
          <Tab icon={<SettingsInputComponent />} iconPosition="start" label="Accesos Invasivos" sx={{ py: 2 }} />
        </Tabs>

        <Box sx={{ p: { xs: 1, md: 3 } }}>
          {/* TAB 1: CLÍNICA Y BALANCE */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <EvolucionCard hospitalizacionId={id} />
              </Grid>
              <Grid item xs={12}>
                <BalanceHidricoCard hospitalizacionId={id} />
              </Grid>
            </Grid>
          )}

          {/* TAB 2: TRATAMIENTO */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MedicacionCard hospitalizacionId={id} />
              </Grid>
              <Grid item xs={12}>
                <NutricionCard hospitalizacionId={id} />
              </Grid>
            </Grid>
          )}

          {/* TAB 3: PROCEDIMIENTOS */}
          {tabValue === 2 && (
            <Box>
              <AccesosInvasivosCard hospitalizacionId={id} variant="full" />
            </Box>
          )}
        </Box>
      </Box>

      <RegistroModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        hospitalizacionId={id}
        onSave={fetchData}
      />
    </Box>
  );
};

export default HospitalizacionDetalle;